import { SERVICES as serviceCatalog } from "@/data/services";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Leaf, Heart, ShieldCheck, Feather, Instagram, Mail, MapPin, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Booking goes through our /intake page, which collects info and hands off to Square.
// Gift cards go through our /gift-cards page, which hands off to Square's e-gift page.
const BOOKING_URL = "/intake";
const GIFT_URL = "/gift-cards";
const CONTACT_EMAIL = "hello@sacredcrownstudio.com";
const INSTAGRAM_URL = "#";

const IMG = {
  hero: "/images/hero.jpg",
  nostalgia: "/images/nostalgia.jpg",
  about: "/images/about.jpg",
  mini: "/images/mini.jpg",
  service1: "/images/service-1.jpg",
  service2: "/images/service-2.jpg",
  service3: "/images/service-3.jpg",
  gift: "/images/gift.jpg",
};

const SERVICE_IMAGES = serviceCatalog.map((s) => s.image);

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: i * 0.08 },
  }),
};

const services = serviceCatalog;

const trustBadges = [
  { icon: Leaf, title: "Nervous System Support" },
  { icon: Heart, title: "Deep Relaxation", subtitle: "& Stress Relief" },
  { icon: ShieldCheck, title: "Women Only", subtitle: "Safe & Professional" },
  { icon: Feather, title: "Nothing Inappropriate.", subtitle: "Just Peace." },
];

const faqs = [
  {
    q: "Is this a salon service?",
    a: "No. Sacred Crown Studio is a relaxation experience designed to help women unwind and recharge. There is no cutting, coloring, or styling.",
  },
  {
    q: "Is this a sexual or intimate service?",
    a: "No. Sacred Crown Studio is a professional women-only relaxation experience. The focus is gentle hair brushing, scalp relaxation, nervous system support, and self-care. There is nothing sexual, suggestive, or inappropriate about the experience.",
  },
  {
    q: "Do I need to download an app?",
    a: "No. Appointments are booked directly through this website in just a few moments.",
  },
  {
    q: "Do I pay when I arrive?",
    a: "No. Payment is collected online at the time of booking, so your appointment can remain a quiet, uninterrupted ritual from beginning to end.",
  },
  {
    q: "Is this women only?",
    a: "Yes. Sacred Crown Studio is an intentionally private, women-only sanctuary, designed as a safe and softly held space for deep rest.",
  },
  {
    q: "What should I wear?",
    a: "Soft, comfortable clothing you can fully relax in. You will be lying back during most of your session.",
  },
];

/* ---------- shared bits ---------- */

const SectionDivider = () => (
  <div className="flex items-center justify-center my-6" aria-hidden>
    <span className="h-px w-10 bg-[#b89461]/50" />
    <span className="mx-3 text-[#b89461] text-xs">✦</span>
    <span className="h-px w-10 bg-[#b89461]/50" />
  </div>
);

const PrimaryButton = ({ href = BOOKING_URL, children = "Book Your Session", testId, className = "" }) => (
  <a
    href={href}
    data-testid={testId}
    className={`group inline-flex items-center justify-center gap-3 rounded-sm bg-[#b89461] px-9 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#fdfbf7] transition-all duration-500 hover:-translate-y-0.5 hover:bg-[#a07e4f] hover:shadow-[0_18px_40px_-18px_rgba(184,148,97,0.55)] ${className}`}
  >
    <span>{children}</span>
  </a>
);

const GhostButton = ({ href = "#about", children, testId }) => (
  <a
    href={href}
    data-testid={testId}
    className="inline-flex items-center justify-center gap-3 rounded-sm border border-[#b89461]/70 px-9 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#7a5e36] transition-all duration-500 hover:border-[#b89461] hover:bg-[#b89461]/10"
  >
    {children}
  </a>
);

/* ---------- page ---------- */

export default function SacredCrown() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <main data-testid="sacred-crown-landing" className="min-h-screen bg-[#fdfbf7] text-[#2d2926] font-body antialiased">
      {/* NAV */}
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-md bg-[#fdfbf7]/85 border-b border-[#eae0d5]/70"
            : "bg-[#fdfbf7]/0"
        }`}
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-6 py-5 sm:px-10">
          <a href="#top" data-testid="brand-mark" className="flex flex-col leading-tight">
            <span className="font-display text-base sm:text-lg tracking-[0.18em] text-[#7a5e36]">
              SACRED CROWN
            </span>
            <span className="font-display text-[10px] tracking-[0.45em] text-[#b89461]">
              STUDIO
            </span>
          </a>
          <div className="hidden md:flex items-center gap-9 text-[11px] tracking-[0.28em] uppercase text-[#5c544f]">
            <a href="#top" className="hover:text-[#b89461] transition-colors">Home</a>
            <a href="#about" className="hover:text-[#b89461] transition-colors">About</a>
            <Link to="/services" className="hover:text-[#b89461] transition-colors">Services</Link>
            <a href="#faq" className="hover:text-[#b89461] transition-colors">FAQ</a>
          </div>
          <a
            href={BOOKING_URL}
            data-testid="nav-book-button"
            className="hidden sm:inline-flex items-center rounded-sm bg-[#b89461] px-5 py-2.5 text-[10px] font-medium tracking-[0.28em] uppercase text-[#fdfbf7] transition-all duration-500 hover:bg-[#a07e4f]"
          >
            Book Your Session
          </a>
        </nav>
      </header>

      {/* HERO */}
      <section id="top" data-testid="hero-section" className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={IMG.hero}
            alt="A woman peacefully receiving gentle hair brushing in warm candlelight"
            className="h-full w-full object-cover object-center"
            loading="eager"
          />
          {/* light ivory veil — never dark */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#fdfbf7]/95 via-[#fdfbf7]/55 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#fdfbf7]/85" />
        </div>

        <div className="relative mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-center px-6 pt-36 pb-24 sm:px-10 md:pb-32">
          <div className="max-w-2xl">
            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              className="font-display text-[11px] tracking-[0.45em] text-[#b89461]"
              data-testid="hero-overline"
            >
              SACRED CROWN STUDIO
            </motion.p>

            <motion.h1
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={1}
              className="mt-8 font-display text-[2.2rem] sm:text-[2.8rem] lg:text-[3.2rem] leading-[1.1] tracking-tight text-[#2d2926]"
              data-testid="hero-headline"
            >
              Hair &amp; Scalp Rituals
              <span className="block italic text-[#7a5e36]">for Rest, Relaxation &amp; Renewal.</span>
            </motion.h1>

            <motion.div initial="hidden" animate="visible" variants={fadeUp} custom={2}>
              <div className="flex items-center gap-3 my-8">
                <span className="h-px w-12 bg-[#b89461]/60" />
                <span className="text-[#b89461]">✦</span>
              </div>
            </motion.div>

            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={3}
              data-testid="hero-subheadline"
              className="max-w-xl text-[15px] sm:text-base leading-[1.85] text-[#3f3a35] font-light"
            >
              Sacred Crown Studio is a women-only relaxation experience dedicated
              to helping you slow down, disconnect from the demands of daily life,
              and experience the comfort of being cared for.
            </motion.p>

            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={4}
              className="mt-5 max-w-xl text-[15px] sm:text-base leading-[1.85] text-[#3f3a35] font-light"
            >
              Each session blends gentle hair brushing, scalp massage,
              aromatherapy, sound healing, Reiki, and intentional relaxation
              techniques to create a calming and restorative experience.
            </motion.p>

            <motion.p
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={5}
              className="mt-5 max-w-xl font-display italic text-[17px] sm:text-lg leading-relaxed text-[#7a5e36]"
            >
              Designed for women who give so much of themselves to others — a rare
              opportunity to pause, unwind, and simply relax.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeUp}
              custom={6}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <PrimaryButton testId="hero-book-button" />
              <GhostButton href="#philosophy" testId="hero-learn-more">Learn More</GhostButton>
            </motion.div>
          </div>
        </div>
      </section>

      {/* OUR PHILOSOPHY — Rest Is Essential */}
      <section id="philosophy" data-testid="philosophy-section" className="bg-[#fdfbf7]">
        <div className="mx-auto max-w-3xl px-6 py-24 sm:px-10 md:py-32 text-center">
          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            className="font-display text-[11px] tracking-[0.45em] text-[#b89461]"
          >
            OUR PHILOSOPHY
          </motion.p>

          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={fadeUp}
            custom={1}
            data-testid="philosophy-headline"
            className="mt-6 font-display text-3xl sm:text-4xl lg:text-[2.6rem] uppercase tracking-tight leading-[1.15] text-[#2d2926]"
          >
            Rest Is <span className="italic text-[#7a5e36] normal-case">Essential</span>
          </motion.h2>

          <SectionDivider />

          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            custom={2}
            className="text-[#3f3a35] text-base sm:text-[17px] leading-[1.95] font-light"
          >
            Modern life asks women to be constantly available, productive, and
            responsible for everyone around them.
          </motion.p>

          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            custom={3}
            className="mt-6 font-display italic text-xl sm:text-2xl leading-relaxed text-[#7a5e36]"
          >
            We believe rest deserves the same priority.
          </motion.p>

          <motion.p
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            custom={4}
            className="mt-8 text-[#3f3a35] text-base sm:text-[17px] leading-[1.95] font-light"
          >
            Sacred Crown Studio was created as a space where women can step away
            from the noise, release tension, and enjoy an experience centered
            entirely on comfort, relaxation, and well-being.
          </motion.p>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            custom={5}
            className="mt-10 mx-auto max-w-md"
          >
            <p className="font-display italic text-xl sm:text-2xl leading-relaxed text-[#2d2926]">
              Because taking care of yourself isn&rsquo;t an indulgence.
            </p>
            <p className="font-display italic text-xl sm:text-2xl leading-relaxed text-[#7a5e36]">
              It&rsquo;s part of staying well.
            </p>
          </motion.div>
        </div>
      </section>

      {/* WHY HAIR & SCALP RITUALS — The Power of Gentle Care */}
      <section id="about" data-testid="about-section" className="bg-[#fdfbf7]">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 sm:px-10 md:grid-cols-2 md:py-32 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden rounded-[1.5rem] shadow-[0_30px_60px_-30px_rgba(122,94,54,0.25)]"
          >
            <img
              src={IMG.nostalgia}
              alt="A still life evoking the gentle ritual of hair and scalp care"
              className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-[1.03]"
              loading="lazy"
            />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="text-center md:text-left"
          >
            <p className="font-display text-[11px] tracking-[0.45em] text-[#b89461]">
              WHY HAIR &amp; SCALP RITUALS?
            </p>
            <h2
              className="mt-5 font-display text-3xl sm:text-4xl lg:text-[2.6rem] uppercase tracking-tight leading-[1.15] text-[#2d2926]"
              data-testid="about-headline"
            >
              The Power of
              <span className="block italic text-[#7a5e36] normal-case">Gentle Care.</span>
            </h2>

            <SectionDivider />

            <p className="text-[#3f3a35] text-base leading-[1.95] font-light">
              There is something uniquely calming about having your hair brushed
              and your scalp gently cared for.
            </p>

            <p className="mt-5 text-[#3f3a35] text-base leading-[1.95] font-light">
              The repetitive motion, soothing touch, and focused attention help
              create a sense of comfort that many women rarely experience in
              their busy lives.
            </p>

            <p className="mt-5 text-[#3f3a35] text-base leading-[1.95] font-light">
              At Sacred Crown Studio, we&rsquo;ve transformed these simple rituals
              into a luxurious self-care experience designed to help quiet the
              mind, relax the body, and create space to simply be.
            </p>

            <p className="mt-6 font-display italic text-lg md:text-xl leading-relaxed text-[#2d2926]">
              For the length of your session, there is nothing to accomplish,
              nowhere to rush, and no one to take care of except yourself.
            </p>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="services" data-testid="services-section" className="bg-[#f6f1ea]/60 border-y border-[#eae0d5]/70">
        <div className="mx-auto max-w-7xl px-6 py-24 sm:px-10 md:py-32">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="text-center"
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.4rem] uppercase tracking-[0.08em] text-[#2d2926]">
              Our Signature Experiences
            </h2>
            <SectionDivider />
          </motion.div>

          <div
            data-testid="services-list"
            className="mt-14 grid gap-8 sm:grid-cols-2 lg:grid-cols-4"
          >
            {services.map((s, idx) => (
              <motion.article
                key={s.name}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.2 }}
                variants={fadeUp}
                custom={idx}
                data-testid={`service-card-${idx}`}
                className="group flex flex-col bg-[#fdfbf7] rounded-[1.25rem] overflow-hidden border border-[#eae0d5]/80 shadow-[0_18px_40px_-30px_rgba(122,94,54,0.25)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_30px_60px_-30px_rgba(122,94,54,0.3)]"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.name}
                    className="h-full w-full object-cover transition-transform duration-[1500ms] group-hover:scale-[1.04]"
                    loading="lazy"
                  />
                </div>
                <div className="flex flex-col flex-1 items-center text-center px-6 py-7">
                  <h3 className="font-display text-lg sm:text-xl uppercase tracking-[0.12em] text-[#2d2926] leading-tight">
                    {s.name}
                  </h3>
                  <p className="mt-2 text-[10px] tracking-[0.35em] uppercase text-[#b89461]">
                    {s.duration} · {s.price}
                  </p>
                  <p className="mt-5 font-display italic text-[15px] leading-relaxed text-[#7a5e36] flex-1">
                    {s.tagline}
                  </p>
                  <div className="mt-7 flex flex-col items-center gap-3 w-full">
                    <Link
                      to="/intake"
                      data-testid={`service-book-${idx}`}
                      className="w-full inline-flex items-center justify-center rounded-sm bg-[#b89461] px-7 py-3 text-[10px] font-medium tracking-[0.32em] uppercase text-[#fdfbf7] transition-all duration-500 hover:bg-[#a07e4f]"
                    >
                      Book Now
                    </Link>
                    <Link
                      to={`/services#${s.id}`}
                      data-testid={`service-details-${idx}`}
                      className="text-[10px] tracking-[0.28em] uppercase text-[#7a5e36] hover:text-[#b89461] inline-flex items-center gap-1"
                    >
                      View details <ChevronRight size={12} />
                    </Link>
                  </div>
                </div>
              </motion.article>
            ))}
          </div>

          {/* trust badges */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={fadeUp}
            className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-6"
            data-testid="trust-badges"
          >
            {trustBadges.map(({ icon: Icon, title, subtitle }) => (
              <div key={title} className="flex flex-col items-center text-center">
                <Icon className="text-[#b89461]" size={32} strokeWidth={1.1} />
                <p className="mt-4 text-[12px] sm:text-[13px] font-medium tracking-[0.05em] text-[#2d2926]">
                  {title}
                </p>
                {subtitle && (
                  <p className="text-[12px] sm:text-[13px] tracking-[0.05em] text-[#5c544f]">
                    {subtitle}
                  </p>
                )}
              </div>
            ))}
          </motion.div>

          {/* View all services link */}
          <div className="mt-14 flex justify-center">
            <Link
              to="/services"
              data-testid="view-all-services"
              className="inline-flex items-center gap-3 rounded-sm border border-[#b89461]/70 px-9 py-4 text-[11px] font-medium tracking-[0.28em] uppercase text-[#7a5e36] hover:bg-[#b89461]/10 transition-all"
            >
              Explore Every Ritual <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* GIFT CERTIFICATES */}
      <section id="gift" data-testid="gift-section" className="bg-[#fdfbf7]">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 py-24 sm:px-10 md:grid-cols-2 md:py-32 md:gap-20 items-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden rounded-[1.5rem] shadow-[0_30px_60px_-30px_rgba(122,94,54,0.25)] order-2 md:order-1"
          >
            <img
              src={IMG.gift}
              alt="A cream gift certificate envelope tied with a soft blush ribbon"
              className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-[1.03]"
              loading="lazy"
            />
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="order-1 md:order-2 text-center md:text-left"
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.6rem] uppercase tracking-tight leading-[1.15] text-[#2d2926]">
              Give The Gift Of
              <span className="block text-[#7a5e36]">Being Cared For.</span>
            </h2>

            <SectionDivider />

            <p className="text-[#3f3a35] text-base leading-[1.95] font-light max-w-lg md:mx-0 mx-auto">
              Perfect for mothers, daughters, sisters, best friends, caregivers,
              birthdays, holidays, and women who deserve a moment of rest.
            </p>

            <div className="mt-10 flex flex-wrap gap-4 justify-center md:justify-start">
              <PrimaryButton href={GIFT_URL} testId="gift-purchase-button">
                Purchase Gift Certificate
              </PrimaryButton>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" data-testid="faq-section" className="bg-[#f6f1ea]/60 border-y border-[#eae0d5]/70">
        <div className="mx-auto max-w-5xl px-6 py-24 sm:px-10 md:py-32">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={fadeUp}
            className="text-center"
          >
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.4rem] uppercase tracking-[0.08em] text-[#2d2926]">
              Frequently Asked Questions
            </h2>
            <SectionDivider />
          </motion.div>

          <Accordion type="single" collapsible className="mt-10 grid md:grid-cols-2 gap-x-10" data-testid="faq-accordion">
            {faqs.map((item, idx) => (
              <AccordionItem
                key={item.q}
                value={`item-${idx}`}
                className="border-b border-[#eae0d5]"
                data-testid={`faq-item-${idx}`}
              >
                <AccordionTrigger className="py-5 text-left font-display text-base sm:text-[17px] tracking-wide text-[#2d2926] hover:no-underline hover:text-[#b89461]">
                  {item.q}
                </AccordionTrigger>
                <AccordionContent className="pb-6 pr-6 text-[#3f3a35] text-[14px] leading-[1.85] font-light">
                  {item.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* FOOTER */}
      <footer id="book" data-testid="footer-section" className="bg-[#fdfbf7]">
        <div className="mx-auto max-w-7xl px-6 sm:px-10 pt-20 pb-10">
          {/* tagline strip */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.9 }}
            className="text-center mb-16"
          >
            <p className="font-display italic text-2xl md:text-3xl leading-relaxed text-[#2d2926]">
              Gentle touch.
              <span className="mx-3 text-[#b89461]">·</span>
              Deep rest.
              <span className="mx-3 text-[#b89461]">·</span>
              Lasting calm.
            </p>
            <p className="mt-4 text-[11px] tracking-[0.35em] uppercase text-[#b89461]">
              Women Only · By Appointment
            </p>
          </motion.div>

          <div className="grid gap-10 md:grid-cols-3 items-start border-t border-[#eae0d5] pt-12">
            {/* brand */}
            <div>
              <div className="flex flex-col leading-tight">
                <span className="font-display text-base tracking-[0.18em] text-[#7a5e36]">
                  SACRED CROWN
                </span>
                <span className="font-display text-[10px] tracking-[0.45em] text-[#b89461]">
                  STUDIO
                </span>
              </div>
              <p className="mt-6 max-w-xs text-[13px] leading-[1.85] text-[#5c544f] font-light">
                You don&rsquo;t have to hold it all together here. This is your time to soften.
              </p>
            </div>

            {/* contact */}
            <div className="space-y-4 text-[14px] text-[#3f3a35] font-light">
              <div className="flex items-start gap-3">
                <ShieldCheck size={16} strokeWidth={1.2} className="mt-1 text-[#b89461]" />
                <div>
                  <p className="text-[11px] tracking-[0.3em] uppercase text-[#b89461]">Women only</p>
                  <p>By appointment</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail size={16} strokeWidth={1.2} className="mt-1 text-[#b89461]" />
                <a
                  href={`mailto:${CONTACT_EMAIL}`}
                  data-testid="footer-contact-email"
                  className="hover:text-[#b89461] transition-colors"
                >
                  {CONTACT_EMAIL}
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={16} strokeWidth={1.2} className="mt-1 text-[#b89461]" />
                <p>Los Angeles, CA</p>
              </div>
              <div className="pt-2 flex items-center gap-4">
                <a
                  href={INSTAGRAM_URL}
                  aria-label="Instagram"
                  data-testid="footer-instagram"
                  className="text-[#7a5e36] hover:text-[#b89461] transition-colors"
                >
                  <Instagram size={18} strokeWidth={1.3} />
                </a>
              </div>
            </div>

            {/* CTA */}
            <div className="md:text-right">
              <p className="font-display italic text-xl text-[#2d2926] mb-6">
                Your hour of quiet is waiting.
              </p>
              <PrimaryButton testId="footer-book-button">Book Your Session</PrimaryButton>
            </div>
          </div>

          <p className="mt-14 text-center text-[11px] tracking-[0.28em] uppercase text-[#b8ad9d]">
            © 2026 Sacred Crown Studio · All Rights Reserved
          </p>
        </div>
      </footer>
    </main>
  );
}
